import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useProjectData } from './hooks';
import { getProcessedProjects } from './utils';
import { CreateProjectModal, ShareFamilyModal, LinkFamilyModal, ProjectSettingsModal } from './components';
import ActionsMenu from '../../components/ActionsMenu';
import { useToolAccess } from '../../tools/ToolAccessContext';

export default function ProjectDashboard() {
    const [searchParams] = useSearchParams();
    const projectIdParam = searchParams.get('projectId');
    const {
        projects,
        isLoading,
        setProjects,
        fetchProjects,
        fetchProjectDetails
    } = useProjectData();

    const [selectedProject, setSelectedProject] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newProject, setNewProject] = useState({ name: '', description: '' });
    const [isEditingMetadata, setIsEditingMetadata] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [sortBy, setSortBy] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [activitySortBy, setActivitySortBy] = useState('date');
    const [activitySortOrder, setActivitySortOrder] = useState('desc');
    const [shareFamily, setShareFamily] = useState(null);
    const [isLinkFamilyOpen, setIsLinkFamilyOpen] = useState(false);
    const [settingsProject, setSettingsProject] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [activeProjectId, setActiveProjectId] = useState(localStorage.getItem('activeProjectId') || '');
    const importFileInputRef = useRef(null);
    const { canEditCurrent } = useToolAccess();

    const toggleSortOrder = () => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');

    const handleSelectProject = async (project) => {
        setIsEditingMetadata(false);
        const currentActiveId = localStorage.getItem('activeProjectId');
        if (currentActiveId !== project.id) {
            localStorage.setItem('activeProjectId', project.id);
            localStorage.setItem('activeProjectName', project.name);
            window.dispatchEvent(new Event('activeProjectChanged'));
        }
        const projectDetails = await fetchProjectDetails(project);
        setSelectedProject(projectDetails);
        return projectDetails;
    };

    const startEditing = (project = selectedProject) => {
        if (!project) return;
        setEditName(project.name);
        setEditDescription(project.description || '');
        setIsEditingMetadata(true);
    };

    const handleEditProject = async (project) => {
        if (!project) return;
        if (!selectedProject || selectedProject.id !== project.id) {
            await handleSelectProject(project);
        }
        startEditing(project);
    };

    const cancelEditing = () => {
        setIsEditingMetadata(false);
    };

    const handleSaveMetadata = async () => {
        if (!selectedProject) return;
        if (!editName.trim()) return;

        try {
            const res = await apiFetch(`/api/projects/${selectedProject.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: editName.trim(),
                    description: editDescription.trim(),
                    allowedOrigins: selectedProject.allowedOrigins || '',
                    expiresAt: selectedProject.expiresAt
                })
            });

            if (res.ok) {
                const updatedProject = await res.json();

                setSelectedProject(prev => {
                    if (prev && prev.id === updatedProject.id) {
                        return {
                            ...prev,
                            name: updatedProject.name,
                            description: updatedProject.description
                        };
                    }
                    return prev;
                });

                setProjects(prev => prev.map(p => p.id === updatedProject.id ? { ...p, name: updatedProject.name, description: updatedProject.description } : p));

                const activeId = localStorage.getItem('activeProjectId');
                if (activeId === updatedProject.id) {
                    localStorage.setItem('activeProjectName', updatedProject.name);
                    window.dispatchEvent(new Event('activeProjectChanged'));
                }

                setIsEditingMetadata(false);
            } else {
                console.error('Failed to save project metadata', await res.text());
            }
        } catch (error) {
            console.error('Failed to save project metadata', error);
        }
    };

    const downloadBlob = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleExportProject = async (project) => {
        const target = project || selectedProject;
        if (!target) return;

        try {
            const res = await apiFetch(`/api/projects/${target.id}/export`);
            if (!res.ok) {
                alert(`Error exporting project: ${await res.text()}`);
                return;
            }
            const blob = await res.blob();
            const fileName = `${String(target.name || 'project').replace(/\s+/g, '_')}.project`;
            downloadBlob(blob, fileName);
        } catch (error) {
            console.error('Failed to export project', error);
            alert(`Failed to export project: ${error.message}`);
        }
    };

    const handleImportClick = () => {
        importFileInputRef.current?.click();
    };

    const handleImportProject = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await apiFetch('/api/projects/import', {
                method: 'POST',
                body: formData
            });
            if (!res.ok) {
                alert(`Error importing project: ${await res.text()}`);
                return;
            }
            const createdProject = await res.json();
            const updatedList = await fetchProjects();
            const match = updatedList.find(p => p.id === createdProject.id) || createdProject;
            handleSelectProject(match);
        } catch (error) {
            console.error('Failed to import project', error);
            alert(`Failed to import project: ${error.message}`);
        } finally {
            e.target.value = '';
        }
    };

    useEffect(() => {
        const handleGlobalProjectChange = () => {
            const activeId = localStorage.getItem('activeProjectId');
            if (activeId) {
                setProjects(prevProjects => {
                    const match = prevProjects.find(p => p.id === activeId);
                    if (match) {
                        setSelectedProject(currSelected => {
                            if (currSelected && currSelected.id === activeId) {
                                return currSelected;
                            }
                            fetchProjectDetails(match);
                            return match;
                        });
                    }
                    return prevProjects;
                });
            } else {
                setSelectedProject(null);
            }
        };
        const syncActive = () => setActiveProjectId(localStorage.getItem('activeProjectId') || '');
        window.addEventListener('activeProjectChanged', handleGlobalProjectChange);
        window.addEventListener('activeProjectChanged', syncActive);
        return () => {
            window.removeEventListener('activeProjectChanged', handleGlobalProjectChange);
            window.removeEventListener('activeProjectChanged', syncActive);
        };
    }, []);

    useEffect(() => {
        const init = async () => {
            const fetchedProjects = await fetchProjects();
            if (!projectIdParam || !fetchedProjects?.length) return;
            const match = fetchedProjects.find(p => String(p.id) === String(projectIdParam));
            if (match) handleSelectProject(match);
        };
        init();
    }, []);

    const handleSaveSettings = async (payload) => {
        if (!settingsProject) return;
        setSavingSettings(true);
        try {
            const res = await apiFetch(`/api/projects/${settingsProject.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                alert(`Failed to save project: ${await res.text()}`);
                return;
            }
            const updated = await res.json();
            setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            if (localStorage.getItem('activeProjectId') === updated.id) {
                localStorage.setItem('activeProjectName', updated.name);
                window.dispatchEvent(new Event('activeProjectChanged'));
            }
            setSettingsProject(null);
        } catch (error) {
            console.error('Failed to save project', error);
            alert(`Failed to save project: ${error.message}`);
        } finally {
            setSavingSettings(false);
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/api/projects', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newProject)
            });
            if (res.ok) {
                const createdProject = await res.json();
                setNewProject({ name: '', description: '' });
                setIsModalOpen(false);
                const updatedList = await fetchProjects();

                const match = updatedList.find(p => p.id === createdProject.id) || createdProject;
                handleSelectProject(match);
            }
        } catch (error) {
            console.error('Failed to create project', error);
        }
    };

    const handleDeleteProject = async (project) => {
        const target = project || selectedProject;
        if (!target) return;
        if (!await window.confirm(`Are you sure you want to permanently delete project '${target.name}'?\nAll its configurations, deployments, and families scope will be deleted. This action cannot be undone.`)) return;

        try {
            const res = await apiFetch(`/api/projects/${target.id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                const activeId = localStorage.getItem('activeProjectId');
                if (activeId === target.id) {
                    localStorage.removeItem('activeProjectId');
                    localStorage.removeItem('activeProjectName');
                    window.dispatchEvent(new Event('activeProjectChanged'));
                }
                const updatedList = await fetchProjects();
                if (updatedList && updatedList.length > 0) {
                    handleSelectProject(updatedList[0]);
                } else {
                    setSelectedProject(null);
                }
            } else {
                console.error('Failed to delete project', await res.text());
            }
        } catch (error) {
            console.error('Failed to delete project', error);
        }
    };

    const refreshSelectedProject = async () => {
        const updatedList = await fetchProjects();
        if (selectedProject) {
            const match = updatedList.find(p => p.id === selectedProject.id) || selectedProject;
            await handleSelectProject(match);
        }
        return updatedList;
    };

    const handleShareFamily = async (family, projectIds) => {
        try {
            for (const projectId of projectIds) {
                const res = await apiFetch(`/api/projects/${projectId}/link-product`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productFamilyId: family.id })
                });
                if (!res.ok) {
                    alert(`Failed to share with a project: ${await res.text()}`);
                    return;
                }
            }
            setShareFamily(null);
            await refreshSelectedProject();
        } catch (error) {
            console.error('Failed to share family', error);
            alert(`Failed to share family: ${error.message}`);
        }
    };

    const handleUnshareFamily = async (family, shareInfo) => {
        const originalName = shareInfo?.originalProject?.name || 'the original project';
        if (!await window.confirm(`Unshare '${family.name}'?\nThis project will keep an independent copy. ${originalName} will keep the original.`)) {
            return;
        }

        try {
            const res = await apiFetch(`/api/projects/${selectedProject.id}/unshare-product`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productFamilyId: family.id })
            });
            if (!res.ok) {
                alert(`Failed to unshare family: ${await res.text()}`);
                return;
            }
            await refreshSelectedProject();
        } catch (error) {
            console.error('Failed to unshare family', error);
            alert(`Failed to unshare family: ${error.message}`);
        }
    };

    const handleUnlinkFamily = async (family) => {
        if (!await window.confirm(`Remove '${family.name}' from this project?\nThe family itself is not deleted.`)) {
            return;
        }

        try {
            const res = await apiFetch(`/api/projects/${selectedProject.id}/unlink-product/${family.id}`, {
                method: 'DELETE'
            });
            if (!res.ok) {
                alert(`Failed to remove family: ${await res.text()}`);
                return;
            }
            await refreshSelectedProject();
        } catch (error) {
            console.error('Failed to unlink family', error);
            alert(`Failed to remove family: ${error.message}`);
        }
    };

    const handleLinkExistingFamilies = async (familyIds) => {
        try {
            for (const familyId of familyIds) {
                const res = await apiFetch(`/api/projects/${selectedProject.id}/link-product`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ productFamilyId: familyId })
                });
                if (!res.ok) {
                    alert(`Failed to link family: ${await res.text()}`);
                    return;
                }
            }
            setIsLinkFamilyOpen(false);
            await refreshSelectedProject();
        } catch (error) {
            console.error('Failed to link families', error);
            alert(`Failed to link family: ${error.message}`);
        }
    };

    const sortedProjects = getProcessedProjects(projects, searchQuery, sortBy, sortOrder);
    const familyCount = projects.reduce((total, project) => total + (project.linkedFamilies?.length || 0), 0);
    const activeProject = projects.find(project => String(project.id) === String(activeProjectId));

    return (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', padding: '1rem 1.25rem', boxSizing: 'border-box', overflow: 'hidden', gap: '0.75rem' }} className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 'var(--radius)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <i className="fa-solid fa-diagram-project" style={{ fontSize: '1.1rem', color: 'var(--accent)' }}></i>
                </div>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0, fontWeight: 700 }}>Dashboard</h1>
                    <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0 0', fontSize: '0.95rem' }}>
                        Create and manage the projects that scope Product, Release, Variant, and Price.
                    </p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem', flexShrink: 0 }}>
                {[
                    { label: 'Total Projects', value: isLoading ? '—' : String(projects.length), change: 'In this organization', changeType: 'info' },
                    { label: 'Linked Families', value: isLoading ? '—' : String(familyCount), change: 'Shared across projects', changeType: 'info' },
                    { label: 'Active Project', value: activeProject?.name || 'None', change: activeProject ? 'Selected in the header' : 'Choose a project above', changeType: 'info' }
                ].map(stat => (
                    <div key={stat.label} style={{
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        padding: '1rem',
                        borderRadius: 'var(--radius)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        minWidth: 0
                    }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                        <div style={{ fontSize: stat.label === 'Active Project' ? '1.15rem' : '1.4rem', fontWeight: 700, marginTop: '0.25rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stat.value}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            <i className="fa-solid fa-circle"></i>
                            <span>{stat.change}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ padding: '0.7rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', flexShrink: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Active Projects</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <input
                            className="input-field"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search projects..."
                            aria-label="Search projects"
                            style={{ margin: 0, minWidth: '220px', padding: '0.45rem 0.75rem' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <ActionsMenu
                                label="Project actions"
                                items={[
                                    {
                                        label: 'Import Project',
                                        icon: 'fa-solid fa-file-import',
                                        onSelect: handleImportClick,
                                        disabled: !canEditCurrent
                                    }
                                ]}
                            />
                            <button
                                type="button"
                                className="submit-btn"
                                onClick={() => setIsModalOpen(true)}
                                disabled={!canEditCurrent}
                                style={{
                                    margin: 0,
                                    padding: '0.5rem 0.75rem',
                                    fontSize: '0.85rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                }}
                            >
                                <i className="fa-solid fa-plus"></i> New
                            </button>
                        </div>
                        <input
                            type="file"
                            ref={importFileInputRef}
                            onChange={handleImportProject}
                            accept=".project,.zip"
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>
                <div style={{ overflow: 'auto', flex: 1 }}>
                    <table className="data-table">
                        <thead>
                            <tr>
                                {['Name', 'Description', 'Families', 'Updated', 'Status'].map(col => (
                                    <th key={col}>{col}</th>
                                ))}
                                <th className="cell-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td className="cell-empty" colSpan={6}>Loading projects…</td>
                                </tr>
                            )}
                            {!isLoading && sortedProjects.length === 0 && (
                                <tr>
                                    <td className="cell-empty" colSpan={6}>No projects yet.</td>
                                </tr>
                            )}
                            {sortedProjects.map((project) => {
                                const isActive = String(project.id) === String(activeProjectId);
                                const updated = project.updatedAt ? new Date(project.updatedAt).toLocaleDateString() : '—';
                                return (
                                    <tr
                                        key={project.id}
                                        className={`table-row-hover clickable-row${isActive ? ' is-selected' : ''}`}
                                        onClick={() => handleSelectProject(project)}
                                        onKeyDown={(event) => {
                                            if (event.target !== event.currentTarget) return;
                                            if (event.key !== 'Enter' && event.key !== ' ') return;
                                            event.preventDefault();
                                            handleSelectProject(project);
                                        }}
                                        tabIndex={0}
                                        aria-selected={isActive}
                                    >
                                        <td className="cell-strong">{project.name}</td>
                                        <td>{project.description || '—'}</td>
                                        <td>{project.linkedFamilies?.length || 0}</td>
                                        <td>{updated}</td>
                                        <td>
                                            <span className={`status-chip${isActive ? ' is-positive' : ''}`}>{isActive ? 'Active' : 'Available'}</span>
                                        </td>
                                        <td className="cell-end" onClick={(event) => event.stopPropagation()}>
                                            <button type="button" className="btn-secondary" onClick={() => setSettingsProject(project)} style={{ padding: '4px 8px', fontSize: '0.8rem', marginRight: '0.5rem' }}>Edit</button>
                                            <ActionsMenu
                                                label={`${project.name} actions`}
                                                compact
                                                items={[
                                                    { label: 'Export', icon: 'fa-solid fa-file-export', onSelect: () => handleExportProject(project) },
                                                    { label: 'Delete', icon: 'fa-solid fa-trash-can', danger: true, onSelect: () => handleDeleteProject(project), disabled: !canEditCurrent }
                                                ]}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <ProjectSettingsModal
                isOpen={!!settingsProject}
                onClose={() => setSettingsProject(null)}
                project={settingsProject}
                onSave={handleSaveSettings}
                onExport={handleExportProject}
                onDelete={handleDeleteProject}
                saving={savingSettings}
            />

            <CreateProjectModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleCreateProject}
                newProject={newProject}
                setNewProject={setNewProject}
            />

            <ShareFamilyModal
                isOpen={!!shareFamily}
                onClose={() => setShareFamily(null)}
                family={shareFamily}
                projects={projects}
                currentProjectId={selectedProject?.id}
                onShare={handleShareFamily}
            />

            <LinkFamilyModal
                isOpen={isLinkFamilyOpen}
                onClose={() => setIsLinkFamilyOpen(false)}
                selectedProject={selectedProject}
                onLink={handleLinkExistingFamilies}
            />
        </div>
    );
}
